import React, { useState } from "react";
import { Flame, Megaphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const RANK = ["bg-gold text-crimson", "bg-stone-300 text-stone-800", "bg-amber-700/80 text-white"];

function Leader({ r, i }) {
  return (
    <li className="flex items-center gap-3 py-2" data-testid={`streak-leader-${r.id}`}>
      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${RANK[i] || "bg-parchment-card text-muted-foreground"}`}>{i + 1}</span>
      <div className="min-w-0 flex-1"><div className="truncate font-medium">{r.name}</div><div className="font-mono-accent text-[11px] text-muted-foreground">{r.member_id} · best {r.best}</div></div>
      <span className="flex items-center gap-1 font-mono-accent text-sm font-bold text-crimson"><Flame size={14} className="text-gold" /> {r.current}</span>
    </li>
  );
}

function ShoutoutDialog({ leaders, onSent }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [limit, setLimit] = useState(5);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reports/streaks/shoutout", { limit, message: msg });
      toast.success(`Shout-out posted for ${data.leaders.length} member(s) and members notified`);
      setOpen(false); setMsg(""); onSent?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" className="gap-1 bg-crimson hover:bg-crimson-hover" onClick={() => setOpen(true)} disabled={leaders.length === 0} data-testid="streak-shoutout-btn"><Megaphone size={14} /> Shout-out</Button>
      <DialogContent data-testid="streak-shoutout-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-crimson">Broadcast streak stars</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Posts a pinned announcement listing the top streaks and notifies every member. Featured members get a personal congratulation.</p>
        <div><Label>How many to feature</Label>
          <div className="mt-1 flex gap-1">{[3, 5, 10].map((n) => <Button key={n} size="sm" variant={limit === n ? "default" : "outline"} className={limit === n ? "bg-crimson" : ""} onClick={() => setLimit(n)} data-testid={`shoutout-limit-${n}`}>Top {n}</Button>)}</div></div>
        <div><Label>Intro message (optional)</Label><Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Ayekoo to the members who keep showing up!" data-testid="shoutout-message" /></div>
        <DialogFooter><Button onClick={send} disabled={busy} className="bg-crimson" data-testid="shoutout-send">{busy && <Loader2 size={14} className="mr-1 animate-spin" />} Post &amp; notify</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StreakLeaderboard() {
  const { data, reload } = useApi("/admin/reports/streaks?limit=10");
  const { user } = useAuth();
  const canShout = (user?.permissions || []).includes("broadcast");
  const leaders = data?.leaders || [];
  return (
    <div className="glass mt-3 rounded-2xl p-5" data-testid="streak-leaderboard">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="flex items-center gap-2 font-serif-display text-lg font-bold text-crimson"><Flame size={18} className="text-gold" /> Attendance streak stars</h4>
          <p className="text-xs text-muted-foreground">Consecutive General Meetings / AGMs attended (2+). {data?.last_shoutout && <>Last shout-out {data.last_shoutout.slice(0, 10)}.</>}</p>
        </div>
        {canShout && <ShoutoutDialog leaders={leaders} onSent={reload} />}
      </div>
      <ul className="mt-2 divide-y divide-gold/15 text-sm">
        {leaders.map((r, i) => <Leader key={r.id} r={r} i={i} />)}
        {data && leaders.length === 0 && <li className="py-4 text-center text-muted-foreground" data-testid="streak-leaderboard-empty">No streaks of 2+ yet — they'll appear after the next General Meeting roll-call.</li>}
      </ul>
    </div>
  );
}
